<script setup lang="ts">
import { useAuthStore, type AuthStore } from '~/stores/auth'
import { useAddresses } from '~/composables/useAddresses'

type AuthView = 'auth' | 'forgot'

const visible = defineModel<boolean>({ default: false })

const { t } = useI18n()
const localePath = useLocalePath()
const router = useRouter()
const api = useApi()
const { showSuccess } = useAppToast()
const authStore = useAuthStore() as AuthStore
// Taken here, not in submitLogin: the order store calls useI18n() when it is created.
const orderStore = useOrderStore()
const { fetchAddresses } = useAddresses()
const activeTab = ref<string>('login')
const authView = ref<AuthView>('auth')

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/


const authEmail = ref('')
const loginForm = reactive({ password: '' })
const loginErrors = reactive({ email: '', password: '' })

const registerForm = reactive({
  password: '',
  firstName: '',
  lastName: '',
  phone: '',
  newsletter: false,
  agreeLegal: false,
})
const registerErrors = reactive({ email: '', password: '', firstName: '', lastName: '', phone: '', legal: '' })

const forgotForm = reactive({ email: '' })
const forgotErrors = reactive({ email: '' })
const forgotSubmitting = ref(false)
const forgotSent = ref(false)
const loginLockState = ref<'none' | 'wrong' | 'locked'>('none')

const registerNotice = ref<'' | 'sent' | 'exists' | 'wait'>('')
const registerSubmitting = ref(false)
const registerEmailShake = ref(false)
const registerLegalShake = ref(false)


const brandingBenefits = computed(() => [
  {
    icon: '/icons/delivery.svg',
    title: t('auth.branding.benefitFastTitle'),
    description: t('auth.branding.benefitFastDescription'),
  },
  {
    icon: '/icons/star-outline.svg',
    title: t('auth.branding.benefitQualityTitle'),
    description: t('auth.branding.benefitQualityDescription'),
  },
  {
    icon: '/icons/cart.svg',
    title: t('auth.branding.benefitVarietyTitle'),
    description: t('auth.branding.benefitVarietyDescription'),
  },
])


function resetForms() {
  authEmail.value = ''
  Object.assign(loginForm, { password: '' })
  Object.assign(loginErrors, { email: '', password: '' })
  Object.assign(registerForm, { password: '', firstName: '', lastName: '', phone: '', newsletter: false, agreeLegal: false })
  Object.assign(registerErrors, { email: '', password: '', firstName: '', lastName: '', phone: '', legal: '' })
  Object.assign(forgotForm, { email: '' })
  Object.assign(forgotErrors, { email: '' })
  forgotSent.value = false
  loginLockState.value = 'none'
  activeTab.value = 'login'
  authView.value = 'auth'
  registerNotice.value = ''
  registerEmailShake.value = false
  registerLegalShake.value = false
}


watch(authEmail, () => {
  loginErrors.email = ''
  registerErrors.email = ''
  if (registerNotice.value === 'wait') registerNotice.value = ''
})
watch(() => loginForm.password, () => {
  loginErrors.password = ''
  loginLockState.value = 'none'
})
watch(() => registerForm.password, () => { registerErrors.password = '' })
watch(() => registerForm.firstName, () => { registerErrors.firstName = '' })
watch(() => registerForm.lastName, () => { registerErrors.lastName = '' })
watch(() => registerForm.phone, () => { registerErrors.phone = '' })
watch(() => registerForm.agreeLegal, () => { registerErrors.legal = '' })
watch(() => forgotForm.email, () => {
  forgotErrors.email = ''
  forgotSent.value = false
})

const requestedView = useState<'auth' | 'forgot' | null>('auth-modal-view', () => null)
const authModalNotice = useState<string | null>('auth-modal-notice', () => null)
const authModalEmail = useState<string | null>('auth-modal-email', () => null)
const authModalStay = useState('auth-modal-stay', () => false)
const checkoutLoginHandoff = useState('checkout-login-handoff', () => false)

function applyAuthPrompt() {
  if (authModalEmail.value) {
    authEmail.value = authModalEmail.value
    forgotForm.email = authModalEmail.value
    activeTab.value = 'login'
  }
  if (requestedView.value === 'forgot') authView.value = 'forgot'
  else if (authModalNotice.value) authView.value = 'auth'
}

watch(visible, (isVisible) => {
  if (!isVisible) {
    resetForms()
    requestedView.value = null
    authModalNotice.value = null
    authModalEmail.value = null
    authModalStay.value = false
    return
  }
  applyAuthPrompt()
})

watch(activeTab, () => {
  Object.assign(loginErrors, { email: '', password: '' })
  Object.assign(registerErrors, { email: '', password: '', firstName: '', lastName: '', phone: '', legal: '' })
  registerEmailShake.value = false
  registerLegalShake.value = false
})

function triggerShake(flag: Ref<boolean>) {
  flag.value = false
  nextTick(() => { flag.value = true })
}


function validateEmail(email: string): string {
  return EMAIL_REGEX.test(email.trim()) ? '' : t('auth.errors.invalidEmail')
}

function validateRequired(value: string): string {
  return value.trim() ? '' : t('auth.errors.requiredPassword')
}

function validateLogin(): boolean {
  loginErrors.email = validateEmail(authEmail.value)
  loginErrors.password = validateRequired(loginForm.password)
  return !loginErrors.email && !loginErrors.password
}

function validateRegister(): boolean {
  registerErrors.email = validateEmail(authEmail.value)
  registerErrors.legal = registerForm.agreeLegal ? '' : t('auth.errors.requiredLegal')
  return !registerErrors.email && !registerErrors.legal
}

function openLoginFromRegister() {
  activeTab.value = 'login'
}

function openForgotFromRegister() {
  forgotForm.email = authEmail.value
  authView.value = 'forgot'
}

function openForgotFromLogin() {
  forgotForm.email = authEmail.value
  Object.assign(loginErrors, { email: '', password: '' })
  loginLockState.value = 'none'
  forgotSent.value = false
  authView.value = 'forgot'
}

function backToAuth(tab: 'login' | 'register') {
  if (forgotForm.email.trim()) authEmail.value = forgotForm.email
  forgotSent.value = false
  Object.assign(loginErrors, { email: '', password: '' })
  loginLockState.value = 'none'
  authView.value = 'auth'
  activeTab.value = tab
}

async function submitRegister() {
  registerNotice.value = ''
  const valid = validateRegister()
  if (registerErrors.email) triggerShake(registerEmailShake)
  if (registerErrors.legal) triggerShake(registerLegalShake)
  if (!valid) return
  registerSubmitting.value = true
  try {
    await api('/Public/ClientUser/invite', {
      method: 'POST',
      body: { email: authEmail.value.trim() },
    })
    registerNotice.value = 'sent'
  } catch (error: any) {
    const status = error?.statusCode ?? error?.status ?? error?.response?.status
    if (status === 409) registerNotice.value = 'exists'
    else if (status === 429) registerNotice.value = 'wait'
    else registerErrors.email = t('auth.register.failed')
  } finally {
    registerSubmitting.value = false
  }
}

function validateForgot(): boolean {
  forgotErrors.email = validateEmail(forgotForm.email)
  return !forgotErrors.email
}


async function submitLogin() {
  if (!validateLogin()) return
  try {
    // The API returns the raw JWT string as the response body.
    // If your backend returns { token: "..." } instead, use: (result as any).token
    const result = await api<{ token: string }>('/login', {
      method: 'POST',
      body: {
        username: authEmail.value,
        password: loginForm.password,
      },
    })

    const stayOnPage = authModalStay.value
    authStore.setToken(result.token)
    if (authStore.user) orderStore.applyAccountContact(authStore.user)
    visible.value = false
    await fetchAddresses(authStore.userId!)
    showSuccess(t('auth.login.successMessage'), t('auth.login.successTitle'))
    if (stayOnPage) {
      checkoutLoginHandoff.value = true
      return
    }
    await router.push(localePath('/account'))
  } catch (error: any) {
    console.error('Login error:', error)
    const detail = String(error?.data?.detail ?? error?.data?.message ?? '').toLowerCase()
    loginErrors.password = ''
    loginLockState.value = detail.includes('locked') ? 'locked' : 'wrong'
  }
}

async function submitForgotPassword() {
  if (!validateForgot()) return
  forgotSubmitting.value = true
  try {
    await api('/Public/ClientUser/forgot-password', {
      method: 'POST',
      body: { email: forgotForm.email },
    })
    Object.assign(loginErrors, { email: '', password: '' })
    loginLockState.value = 'none'
    forgotSent.value = true
  } catch (error) {
    console.error('Forgot password error:', error)
    forgotErrors.email = t('auth.forgotPassword.errorMessage')
  } finally {
    forgotSubmitting.value = false
  }
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    :dismissable-mask="true"
    :closable="false"
    unstyled
    :aria-label="t('auth.modalTitle')"
    pt:root:class="auth-dialog-root"
    pt:mask:class="auth-dialog-mask"
  >
    <template #container="{ closeCallback }">
      <section class="auth-modal">
        <button
          type="button"
          class="close-btn"
          :aria-label="t('auth.close')"
          @click="closeCallback"
        >
          ×
        </button>

        <aside class="branding-panel">
          <div class="branding-logo-slot">
            <img
              class="branding-logo"
              src="/images/logo/logo-vodi4ka-header.png"
              alt="Vodi4ka"
              width="124"
              height="111"
            />
          </div>
          <div class="branding-copy">
          <h2>{{ t('auth.branding.title') }}</h2>
          <ul>
            <li v-for="benefit in brandingBenefits" :key="benefit.title">
              <span class="branding-icon">
                <span
                  class="branding-icon-glyph"
                  role="img"
                  :aria-label="benefit.title"
                  :style="{ '--icon': `url('${benefit.icon}')` }"
                />
              </span>
              <div>
                <h3>{{ benefit.title }}</h3>
                <p>{{ benefit.description }}</p>
              </div>
            </li>
          </ul>
          </div>
          <div class="branding-logo-slot" aria-hidden="true"></div>
        </aside>

        <div class="form-panel">
          <!-- Forgot Password View -->
          <template v-if="authView === 'forgot'">
            <div class="auth-form">
              <div class="tabs forgot-tabs">
                <button type="button" class="tab-btn active">
                  {{ t('auth.forgotPassword.title') }}
                </button>
                <button type="button" class="tab-btn" @click="backToAuth('login')">
                  {{ t('auth.tabs.login') }}
                </button>
              </div>
              <form class="forgot-form" @submit.prevent="submitForgotPassword">
                <div class="field">
                  <FormInput
                    id="auth-forgot-email"
                    v-model="forgotForm.email"
                    icon="tabler:mail"
                    type="email"
                    autocomplete="email"
                    :placeholder="t('auth.forgotPassword.emailPlaceholder')"
                  />
                  <p v-if="forgotErrors.email" class="auth-callout auth-callout--error">{{ forgotErrors.email }}</p>
                </div>

                <p v-if="forgotSent" class="auth-callout auth-callout--success">
                  {{ t('auth.forgotPassword.sentNotice') }}
                </p>

                <button v-if="!forgotSent" type="submit" class="cta-btn cta-login" :disabled="forgotSubmitting">
                  {{ forgotSubmitting ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.submit') }}
                </button>
                <button v-else type="button" class="cta-btn cta-login" @click="backToAuth('login')">
                  {{ t('auth.forgotPassword.backToLogin') }}
                </button>
              </form>
            </div>
          </template>

          <!-- Login / Register Tabs -->
          <template v-else>
            <Tabs v-model:value="activeTab" unstyled>
              <TabList unstyled class="tabs">
                <Tab value="login" unstyled class="tab-btn" :class="{ active: activeTab === 'login' }">
                  {{ t('auth.tabs.login') }}
                </Tab>
                <Tab value="register" unstyled class="tab-btn" :class="{ active: activeTab === 'register' }">
                  {{ t('auth.tabs.register') }}
                </Tab>
              </TabList>

              <TabPanels unstyled>
                <!-- Login Panel -->
                <TabPanel value="login" unstyled>
                  <form class="auth-form" @submit.prevent="submitLogin">
                    <p v-if="authModalNotice" class="auth-callout auth-callout--info">{{ authModalNotice }}</p>
                    <div class="field">
                      <FormInput
                        id="auth-login-email"
                        v-model="authEmail"
                        icon="tabler:mail"
                        type="email"
                        autocomplete="email"
                        :placeholder="t('auth.fields.emailPlaceholder')"
                      />
                      <p v-if="loginErrors.email" class="auth-callout auth-callout--error">{{ loginErrors.email }}</p>
                    </div>

                    <div class="field">
                      <FormInput
                        id="auth-login-password"
                        v-model="loginForm.password"
                        icon="tabler:lock"
                        type="password"
                        autocomplete="current-password"
                        :placeholder="t('auth.fields.passwordPlaceholder')"
                      />
                      <p v-if="loginLockState === 'wrong'" class="auth-callout auth-callout--error">{{ t('auth.errors.invalidCredentials') }}</p>
                      <p v-else-if="loginLockState === 'locked'" class="auth-callout auth-callout--error">
                        <i18n-t keypath="auth.errors.accountLocked" tag="span">
                          <template #forgot>
                            <button type="button" class="forgot-inline" @click="openForgotFromLogin">
                              {{ t('auth.login.forgotPassword') }}
                            </button>
                          </template>
                        </i18n-t>
                      </p>
                      <p v-else-if="loginErrors.password" class="auth-callout auth-callout--error">{{ loginErrors.password }}</p>
                    </div>

                    <button type="button" class="forgot-password" @click="openForgotFromLogin">
                      {{ t('auth.login.forgotPassword') }}
                    </button>

                    <button type="submit" class="cta-btn cta-login">
                      {{ t('auth.login.submit') }}
                    </button>

                    <div class="divider">{{ t('auth.login.socialDivider') }}</div>
                    <div class="social-buttons">
                      <button type="button" class="social-btn" :aria-label="t('auth.social.facebook')">
                        <img src="/icons/facebook-color.svg" alt="Facebook" />
                      </button>
                      <button type="button" class="social-btn" :aria-label="t('auth.social.google')">
                        <img src="/icons/google.svg" alt="Google" />
                      </button>
                    </div>
                  </form>
                </TabPanel>

                <!-- Register Panel -->
                <TabPanel value="register" unstyled>
                  <form class="auth-form" novalidate @submit.prevent="submitRegister">
                    <div class="field">
                      <FormInput
                        id="auth-register-email"
                        v-model="authEmail"
                        icon="tabler:mail"
                        type="email"
                        autocomplete="email"
                        :placeholder="t('auth.fields.emailPlaceholder')"
                        :has-error="!!registerErrors.email"
                        :shake="registerEmailShake"
                        @animationend="registerEmailShake = false"
                      />
                      <p v-if="registerErrors.email" class="auth-callout auth-callout--error">{{ registerErrors.email }}</p>
                    </div>

                    <div class="checkboxes">
                      <label
                        class="legal-check"
                        :class="{ 'has-error': !!registerErrors.legal, shake: registerLegalShake }"
                        @animationend="registerLegalShake = false"
                      >
                        <input
                          v-model="registerForm.agreeLegal"
                          type="checkbox"
                        />
                        <span>
                          {{ t('auth.register.agreePrefix') }}
                          <NuxtLink :to="localePath('/terms')" target="_blank">{{ t('auth.register.termsLink') }}</NuxtLink>
                          {{ t('auth.register.agreeAnd') }}
                          <NuxtLink :to="localePath('/privacy')" target="_blank">{{ t('auth.register.privacyLink') }}</NuxtLink>
                        </span>
                      </label>
                    </div>
                    <p v-if="registerErrors.legal" class="auth-callout auth-callout--error">{{ registerErrors.legal }}</p>

                    <p v-if="registerNotice === 'sent' || registerNotice === 'wait'" class="auth-callout auth-callout--success">
                      {{ registerNotice === 'sent' ? t('auth.register.sent') : t('auth.register.wait') }}
                    </p>
                    <div v-else-if="registerNotice === 'exists'" class="auth-callout auth-callout--info auth-callout--stack">
                      <p>{{ t('auth.register.exists') }}</p>
                      <button type="button" class="forgot-password" @click="openLoginFromRegister">
                        {{ t('auth.tabs.login') }}
                      </button>
                      <button type="button" class="forgot-password" @click="openForgotFromRegister">
                        {{ t('auth.login.forgotPassword') }}
                      </button>
                    </div>

                    <button type="submit" class="cta-btn cta-register" :disabled="registerSubmitting">
                      {{ registerSubmitting ? t('auth.register.sending') : t('auth.register.submit') }}
                    </button>

                    <div class="divider">{{ t('auth.register.socialDivider') }}</div>
                    <div class="social-buttons">
                      <button type="button" class="social-btn" :aria-label="t('auth.social.facebook')">
                        <img src="/icons/facebook-color.svg" alt="Facebook" />
                      </button>
                      <button type="button" class="social-btn" :aria-label="t('auth.social.google')">
                        <img src="/icons/google.svg" alt="Google" />
                      </button>
                    </div>
                  </form>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </template>
        </div>
      </section>
    </template>
  </Dialog>
</template>

<style>
.auth-dialog-mask {
  background: rgba(10, 51, 111, 0.5) !important;
}

@media (max-width: 960px) {
  .auth-dialog-mask {
    align-items: flex-start !important;
    overflow-y: auto !important;
    padding-block: 1rem;
  }

  .auth-dialog-root {
    max-height: none !important;
    flex-shrink: 0;
  }
}
</style>

<style scoped>
.auth-modal {
  width: min(1000px, 100vw - 2rem);
  min-height: 640px;
  background: var(--white);
  border-radius: 28px;
  border: 1px solid var(--ligth-blue);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr 1fr;
  position: relative;
  color-scheme: light;
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background-color: var(--ligth-blue);
  color: var(--dark-blue);
  font-size: 1.25rem;
  cursor: pointer;
  z-index: 1;
}

.branding-panel {
  background-image: linear-gradient(
    165deg,
    rgb(248, 206, 133) 0%,
    rgb(216, 34, 104) 100%
  );
  padding: 0 2rem;
  display: flex;
  flex-direction: column;
  color: var(--white);
  position: relative;
}

.branding-logo-slot {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
}

.branding-logo {
  height: 88px;
  width: auto;
  object-fit: contain;
}

.branding-copy {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.branding-panel > * {
  position: relative;
  z-index: 1;
}

.branding-panel h2 {
  color: var(--white);
  font-size: 28px;
  line-height: 1.2;
  font-weight: 800;
}

.branding-panel ul {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.branding-panel li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.branding-panel li .branding-icon {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: var(--white);
  border-radius: 50%;
  padding: 0.45rem;
  box-sizing: border-box;
}

.branding-panel li .branding-icon-glyph {
  width: 100%;
  height: 100%;
  background-color: var(--dark-blue);
  -webkit-mask-image: var(--icon);
  mask-image: var(--icon);
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
}

.branding-panel h3 {
  color: var(--white);
  font-size: 15px;
  font-family: Montserrat, ui-sans-serif, system-ui, sans-serif;
}

.branding-panel p {
  color: rgba(255, 255, 255, 0.95);
  font-size: 13px;
  margin-top: 0.1rem;
}

.form-panel {
  padding: 5.4rem 2.2rem 2rem;
}

.tabs {
  display: flex;
  align-items: center;
  border: none;
  padding: 0;
  width: fit-content;
  margin: 0 auto 1.5rem;
}

.tab-btn {
  border: none;
  background: transparent;
  color: var(--dark-blue);
  font-weight: 700;
  font-family: Montserrat, ui-sans-serif, system-ui, sans-serif;
  padding: 0.55rem 1.1rem;
  border-radius: 999px;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.tab-btn + .tab-btn {
  margin-left: 1.6rem;
  position: relative;
}

.tab-btn + .tab-btn::before {
  content: '';
  position: absolute;
  left: -0.85rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 22px;
  background-color: var(--dark-blue);
}

.tab-btn.active {
  background-color: var(--dark-blue);
  color: var(--white);
  text-decoration: none;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.auth-form :deep(.input-wrapper) {
  min-height: 40px;
}

.auth-form :deep(.input-wrapper input),
.auth-form :deep(.input-wrapper textarea) {
  color: var(--blue) !important;
}

.auth-form :deep(.input-wrapper input) {
  min-height: 36px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.forgot-password,
.forgot-inline {
  width: fit-content;
  align-self: flex-end;
  border: none;
  background: transparent;
  color: var(--dark-blue);
  font-size: 13px;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.forgot-inline {
  display: inline;
  align-self: auto;
  font-size: inherit;
  padding: 0;
}

.forgot-tabs {
  margin-bottom: 1rem;
}

.forgot-tabs .tab-btn + .tab-btn {
  margin-left: 2rem;
}

.forgot-tabs .tab-btn + .tab-btn::before {
  left: -1.05rem;
}

.forgot-form {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  width: 100%;
}

.forgot-form > .cta-btn {
  margin-top: 1.3rem;
}

.auth-form:has(.forgot-tabs) {
  gap: 1.2rem;
}

.cta-btn {
  width: 100%;
  border: none;
  border-radius: 999px;
  color: var(--white);
  font-family: Montserrat, ui-sans-serif, system-ui, sans-serif;
  font-weight: 700;
  font-size: 14px;
  padding: 0.75rem 1rem;
  cursor: pointer;
}

.cta-login {
  background-color: var(--dark-blue);
}

.cta-register {
  background-color: var(--ligth-blue);
  color: var(--dark-blue);
}

.cta-register:not(:disabled) {
  background-color: var(--dark-blue);
  color: var(--white);
}

.cta-register:disabled {
  background-color: #DAEFFF;
  color: var(--dark-blue);
  opacity: 1;
}

.cta-btn:disabled:not(.cta-register) {
  cursor: not-allowed;
  opacity: 0.55;
}

.divider {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-top: 0.2rem;
  text-align: center;
  color: rgba(38, 75, 129, 0.75);
  font-size: 13px;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1 1 0;
  min-width: 0.75rem;
  height: 1px;
  background-color: var(--ligth-blue);
}

.social-buttons {
  display: flex;
  justify-content: center;
  gap: 0.6rem;
}

.social-btn {
  border: 1px solid var(--dark-blue);
  background-color: var(--white);
  border-radius: 999px;
  width: 150px;
  min-height: 42px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.social-btn img {
  width: 20px;
  height: 20px;
}

.checkboxes {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-top: 0.2rem;
}

.checkboxes label {
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.45;
}

.checkboxes label span {
  min-width: 0;
  flex: 1;
}

.legal-check {
  box-sizing: border-box;
  width: 100%;
  padding: 0.35rem 0.5rem;
  border: 2px solid transparent;
  border-radius: 8px;
}

.legal-check.has-error {
  border-color: var(--accent-red);
}

.legal-check.shake {
  animation: field-shake 0.35s ease;
}

.checkboxes a {
  color: var(--dark-blue);
  text-decoration: underline;
}

.checkboxes input {
  margin-top: 0.1rem;
  accent-color: var(--dark-blue);
  background-color: var(--white);
}

.auth-callout {
  margin: 0;
  padding: 0.55rem 0.75rem;
  border-radius: 10px;
  border: none;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
}

.auth-callout--error {
  background: #fdeef3;
  color: var(--accent-red);
}

.auth-callout--success {
  background: #d1e7dd;
  color: #0f5132;
}

.auth-callout--info {
  background: #e8f4ff;
  color: var(--dark-blue);
}

.auth-callout--stack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
}

.auth-callout--stack p {
  margin: 0;
}

.auth-callout--stack .forgot-password {
  align-self: flex-start;
}

@media (max-width: 960px) {
  .auth-modal {
    grid-template-columns: 1fr;
    min-height: auto;
    max-height: none;
    overflow: hidden;
  }

  .form-panel,
  .branding-panel {
    min-width: 0;
  }

  .form-panel {
    order: 1;
    padding: 3.6rem 1.2rem 1.4rem;
  }

  .branding-panel {
    order: 2;
    padding: 1.5rem 1.25rem 1.75rem;
    overflow-wrap: break-word;
  }

  .branding-logo-slot {
    flex: none;
    min-height: auto;
  }

  .branding-logo-slot[aria-hidden='true'] {
    display: none;
  }

  .branding-logo {
    height: 72px;
    margin-bottom: 1.25rem;
  }

  .branding-panel h2 {
    font-size: 24px;
  }

  .branding-panel li > div {
    flex: 1;
    min-width: 0;
    overflow-wrap: break-word;
  }

  .tabs:not(.forgot-tabs) {
    max-width: 100%;
  }

  .tabs:not(.forgot-tabs) :deep([role="tablist"]) {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    max-width: 100%;
  }

  .tabs:not(.forgot-tabs) :deep([data-pc-section="activebar"]) {
    position: absolute;
  }

  .tabs:not(.forgot-tabs) .tab-btn {
    font-size: 14px;
    padding: 0.45rem 0.65rem;
    white-space: nowrap;
  }

  .tabs:not(.forgot-tabs) .tab-btn + .tab-btn {
    margin-left: 0.75rem;
  }

  .tabs:not(.forgot-tabs) .tab-btn + .tab-btn::before {
    left: -0.375rem;
  }
}
</style>

